import React from 'react'
import { Loading } from '@pandiafi/design-system'
import { RouteObject } from 'react-router-dom'

import { Layout } from '../layouts'
import routePath from './routePath'
import { Permissions } from '../components/Permissions'
import { DenyAccess } from '../components/DenyAccess/DenyAccess'
import { DefectNoticeEditView } from '../views/DefectNotice/DefectNoticeEditView/DefectNoticeEditView'
import { BuildingView } from '../views/Building/BuildingView'
import { ConfirmContractModification } from '../views/ContractChanges/ModificationConfirm/Confirm'
import { FeedbackView } from '../views/Feedback/FeedbackView/FeedbackView'
import { QuestionView } from '../views/Question/QuestionView/QuestionView'
import { MessagingGate } from './guards/MessagingGate'
import { DefectNoticeGate } from './guards/DefectNoticeGate'
import { ParkingSpotsGate } from './guards/ParkingSpotsGate'
import { SaunaGate } from './guards/SaunaGate'
import { GymGate } from './guards/GymGate'
import { LaundryReservationGate } from './guards/LaundryReservationGate'
import { StorageGate } from './guards/StorageReservationGate'
import { CommonAreaReservationsGate } from './guards/CommonAreaReservationsGate'
import { GymReservationsGate } from './guards/GymReservationsGate'
import { GymReservations } from '../views/GymReservations'
import { FeedbackGate } from './guards/FeedbackGate'
import { QuestionGate } from './guards/QuestionGate'
import { ResidentChangesGate } from './guards/ResidentChangesGate'

import { Storages } from 'views/Storages'
import { ContactInfo } from 'views/ContactInfo'
import { Documents } from 'views/Documents'
import { FrontPageView } from 'views/FrontPage/FrontPageView'
import { Payments } from 'views/Payments'
import { PaymentHistory } from 'views/Payments/PaymentHistory'
import { PaymentInformation } from 'views/Payments/PaymentInformation'
import { PaymentsAccessDeniedPage } from 'views/Payments/PaymentsAccessDeniedPage'
import { TerminateContract } from 'views/TerminateContract'
import { ResidentChanges } from 'views/ResidentChanges'
import { ConfirmContractTermination } from 'views/TerminateContract/Confirm/Confirm'
import { BulletinView } from 'views/BulletinView/BulletinView'
import { UserInfo } from 'views/UserInfo'
import { BulletinBoardView } from 'views/BulletinBoard/BulletinBoardView'
import { BulletinBoardBulletinView } from 'views/BulletinBoard/BulletinBoardBulletinView'
import { Messages } from 'views/Messages'
import { MessageThread } from 'views/MessageThread'
import { Feedback } from 'views/Feedback'
import { Question } from 'views/Question'
import { ParkingSpots } from 'views/ParkingSpots'
import { LaundryReservations } from 'views/LaundryReservations'
import { SaunaReservations } from 'views/SaunaReservations'
import { GymBooking } from 'views/GymBooking'
import { CommonAreaReservations } from 'views/CommonAreaReservations'
import { DefectNoticeView } from 'views/DefectNotice'

// All of these routes are behind authentication: cognito user WITH tenant must be found
export const authenticatedRoutes = (): RouteObject => {
  return {
    element: <Layout variant="authenticated" />,
    children: [
      {
        id: 'frontPageRoute',
        path: routePath.frontPage,
        element: <FrontPageView />,
      },
      {
        id: 'paymentsRoute',
        element: (
          <Permissions
            feature="payments"
            fallback={<PaymentsAccessDeniedPage />}
          >
            <Payments />
          </Permissions>
        ),
        children: [
          {
            id: 'paymentHistoryRoute',
            path: routePath.paymentHistory,
            element: <PaymentHistory />,
          },
          {
            id: 'bankAccountsRoute',
            path: routePath.paymentInfo,
            element: <PaymentInformation />,
          },
        ],
      },
      {
        id: 'terminateContractRoute',
        path: routePath.terminateContract,
        element: (
          <Permissions feature="contractChanges" fallback={<DenyAccess />}>
            <TerminateContract />
          </Permissions>
        ),
      },
      {
        id: 'residentChangesRoute',
        path: routePath.residentChanges,
        element: (
          <Permissions feature="contractChanges" fallback={<DenyAccess />}>
            <ResidentChangesGate>
              <ResidentChanges />
            </ResidentChangesGate>
          </Permissions>
        ),
      },
      {
        id: 'parkingRoute',
        path: routePath.parking,
        element: (
          <ParkingSpotsGate>
            <ParkingSpots />
          </ParkingSpotsGate>
        ),
      },
      {
        id: 'storagesRoute',
        path: routePath.storages,
        element: (
          <StorageGate>
            <Storages />
          </StorageGate>
        ),
      },
      {
        id: 'laundryReservationsRoute',
        path: routePath.laundryReservations,
        element: (
          <LaundryReservationGate>
            <LaundryReservations />
          </LaundryReservationGate>
        ),
      },
      {
        id: 'saunaReservationsRoute',
        path: routePath.saunaReservations,
        element: (
          <SaunaGate>
            <SaunaReservations />
          </SaunaGate>
        ),
      },
      {
        id: 'gymBookingRoute',
        path: routePath.gymBooking,
        element: (
          <GymGate>
            <GymBooking />
          </GymGate>
        ),
      },
      {
        id: 'gymReservationsRoute',
        path: routePath.gymReservations,
        element: (
          <GymReservationsGate>
            <GymReservations />
          </GymReservationsGate>
        ),
      },
      {
        id: 'commonAreaReservationsRoute',
        path: routePath.commonAreaReservations,
        element: (
          <CommonAreaReservationsGate>
            <CommonAreaReservations />
          </CommonAreaReservationsGate>
        ),
      },
      // Messaging only if feature flag + config allow
      {
        id: 'messagesRoute',
        path: routePath.messages,
        element: (
          <MessagingGate>
            <Messages />
          </MessagingGate>
        ),
      },
      {
        id: 'messageThreadRoute',
        path: routePath.messageThread,
        element: (
          <MessagingGate>
            <MessageThread />
          </MessagingGate>
        ),
      },
      {
        id: 'confirmContractTerminationRoute',
        path: routePath.confirmContractTermination,
        element: (
          <Permissions feature="contractChanges" fallback={<DenyAccess />}>
            <ConfirmContractTermination />
          </Permissions>
        ),
      },
      {
        id: 'confirmContractModificationRoute',
        path: routePath.confirmContractModification,
        element: (
          <Permissions feature="contractChanges" fallback={<DenyAccess />}>
            <ConfirmContractModification />
          </Permissions>
        ),
      },
      {
        id: 'documentsRoute',
        path: routePath.documents,
        element: <Documents />,
      },
      {
        id: 'buildingRoute',
        path: routePath.building,
        element: <BuildingView />,
      },
      {
        id: 'contactInfoRoute',
        path: routePath.contactInfo,
        element: <ContactInfo />,
      },
      {
        id: 'userInfoRoute',
        path: routePath.userInfo,
        element: <UserInfo />,
      },
      // Defect notice routes are gated by config (and contractTerminationOnly rule)
      {
        id: 'defectNoticeEditRoute',
        path: routePath.defectNoticeEdit,
        element: (
          <DefectNoticeGate>
            <DefectNoticeEditView />
          </DefectNoticeGate>
        ),
      },
      {
        id: 'defectNoticeRoute',
        path: routePath.defectNotice,
        element: (
          <DefectNoticeGate>
            <DefectNoticeView />
          </DefectNoticeGate>
        ),
      },
      {
        id: 'feedbackViewRoute',
        path: routePath.feedbackView,
        element: (
          <FeedbackGate>
            <FeedbackView />
          </FeedbackGate>
        ),
      },
      {
        id: 'questionViewRoute',
        path: routePath.questionView,
        element: (
          <QuestionGate>
            <QuestionView />
          </QuestionGate>
        ),
      },
      {
        id: 'questionRoute',
        path: routePath.question,
        element: (
          <QuestionGate>
            <Question />
          </QuestionGate>
        ),
      },
      {
        id: 'feedbackRoute',
        path: routePath.feedback,
        element: (
          <FeedbackGate>
            <Feedback />
          </FeedbackGate>
        ),
      },
      {
        id: 'bulletinRoute',
        path: routePath.bulletin,
        element: <BulletinView />,
      },
      {
        id: 'bulletinBoardRoute',
        path: routePath.bulletinBoard,
        element: <BulletinBoardView />,
      },
      {
        id: 'bulletinBoardBulletinRoute',
        path: routePath.bulletinBoardBulletin,
        element: <BulletinBoardBulletinView />,
      },
      {
        // AuthenticatedDataLoader will redirect to frontPage if no contractId is found
        id: 'rootRoute',
        path: routePath.root,
        element: <Loading variant="fullPage" />,
      },
    ],
  }
}
